import { useEffect, useState } from 'react'
import { ArrowDown, ArrowUp, X } from 'lucide-react'
import {
  catalogEffortLevels,
  type CompactionDefaults,
  effortLevelLabel,
  FALLBACK_COMPACTION_DEFAULTS,
  MODEL_EFFORT_LEVEL_OPTIONS,
  type ModelCandidate,
  type ModelEffort,
  type ModelOption,
  type ModelProvider,
  ModelTaskClass,
  type OrganizationModelPolicy,
  type TaskClassRoutingState,
  type UpdateOrganizationModelPolicyPayload,
  useGetModelPolicyQuery,
  useUpdateModelPolicyMutation
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { Checkbox } from '../../../components/ui/checkbox'
import { Input } from '../../../components/ui/input'
import { Switch } from '../../../components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs'
import { useDebugMode } from '../../../lib/debug-mode'
import {
  allowedModelOptions,
  isModelAllowed,
  modelLabel,
  modelTargetKey
} from '../../../lib/model-policy-labels'

const POLICY_CLASSES: ReadonlyArray<{
  name: ModelTaskClass
  label: string
  description: string
}> = [
  {
    name: ModelTaskClass.STANDARD,
    label: 'Standard',
    description: 'Chat and general analysis'
  },
  {
    name: ModelTaskClass.HEAVY,
    label: 'Heavy',
    description: 'Deep ingest and complex analysis'
  },
  {
    name: ModelTaskClass.DASHBOARD,
    label: 'Dashboard',
    description: 'Dashboard generation and editing'
  },
  {
    name: ModelTaskClass.LIGHT,
    label: 'Light',
    description: 'Fast, lightweight tasks'
  }
]

const POLICY_DEFAULT_EFFORT = {
  value: 'default' as const,
  label: 'Provider default'
}

function effortChoicesForModel(
  catalog: ModelOption[],
  provider: string,
  model: string
): Array<{ value: ModelEffort; label: string }> {
  const levels = catalogEffortLevels(
    catalog.find((option) => option.provider === provider && option.model === model)
  )
  return [
    POLICY_DEFAULT_EFFORT,
    ...MODEL_EFFORT_LEVEL_OPTIONS.filter((option) => levels.includes(option.value))
  ]
}

/**
 * Parse the window-relative trigger typed as a percentage. Empty means
 * "product default". Returns the fraction the proxy stores; the accepted
 * range comes from the policy's `compactionDefaults` (proxy-owned limits).
 */
export function parseCompactionFractionPercent(
  value: string,
  bounds: CompactionDefaults = FALLBACK_COMPACTION_DEFAULTS
): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number(trimmed)
  const minPercent = Math.round(bounds.minTriggerFraction * 100)
  const maxPercent = Math.round(bounds.maxTriggerFraction * 100)
  if (!Number.isFinite(parsed) || parsed < minPercent || parsed > maxPercent) {
    throw new Error(
      `Auto-compaction window share must be between ${minPercent}% and ${maxPercent}%.`
    )
  }
  return Math.round(parsed) / 100
}

/** Unset window share as a whole percent, from the proxy-owned product default. */
function defaultWindowSharePercent(bounds: CompactionDefaults): number {
  return Math.round(bounds.triggerFraction * 100)
}

function fractionToPercentDraft(
  fraction: number | null | undefined,
  bounds: CompactionDefaults = FALLBACK_COMPACTION_DEFAULTS
): string {
  if (fraction === null || fraction === undefined) {
    return String(defaultWindowSharePercent(bounds))
  }
  return String(Math.round(fraction * 100))
}

export function parseCompactionTrigger(
  value: string,
  bounds: CompactionDefaults = FALLBACK_COMPACTION_DEFAULTS
): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number(trimmed)
  if (
    !Number.isInteger(parsed) ||
    parsed < bounds.minTriggerInputTokens ||
    parsed > bounds.maxTriggerInputTokens
  ) {
    throw new Error(
      `Auto-compaction threshold must be between ${bounds.minTriggerInputTokens.toLocaleString(
        'en-US'
      )} and ${bounds.maxTriggerInputTokens.toLocaleString('en-US')} tokens.`
    )
  }
  return parsed
}

function TaskClassRoutingBlock({
  taskClass,
  label,
  description,
  config,
  providers,
  catalog,
  policy,
  disabled,
  onSave
}: {
  taskClass: ModelTaskClass
  label: string
  description: string
  config: TaskClassRoutingState
  providers: ModelProvider[]
  catalog: ModelOption[]
  policy: OrganizationModelPolicy
  disabled: boolean
  onSave: (payload: UpdateOrganizationModelPolicyPayload) => void
}) {
  const candidates = config.effectivePolicy.candidates
  const resolvedTarget = config.resolvedTarget
  const providerByName = new Map(providers.map((provider) => [provider.name, provider]))
  const selectableProviderNames = new Set(
    providers.filter((provider) => provider.available).map((provider) => provider.name)
  )
  const selectableCatalog = allowedModelOptions(policy).filter((option) =>
    selectableProviderNames.has(option.provider)
  )

  const saveCandidates = (next: ModelCandidate[]): void => {
    onSave({ taskClassRouting: { [taskClass]: { candidates: next } } })
  }

  const moveCandidate = (index: number, direction: -1 | 1): void => {
    const nextIndex = index + direction
    if (nextIndex < 0 || nextIndex >= candidates.length) return
    const next = [...candidates]
    ;[next[index], next[nextIndex]] = [next[nextIndex]!, next[index]!]
    saveCandidates(next)
  }

  const selectModel = (index: number, key: string): void => {
    const option = selectableCatalog.find(
      (item) => modelTargetKey(item.provider, item.model) === key
    )
    const current = candidates[index]
    if (!option || !current || modelTargetKey(current.provider, current.model) === key) return
    const acceptedEfforts = catalogEffortLevels(option)
    const effort =
      current.effort === 'default' || acceptedEfforts.includes(current.effort)
        ? current.effort
        : 'default'
    const next = [...candidates]
    next[index] = { provider: option.provider, model: option.model, effort }
    saveCandidates(next)
  }

  const selectEffort = (index: number, effort: ModelEffort): void => {
    const current = candidates[index]
    if (!current || current.effort === effort) return
    const next = [...candidates]
    next[index] = { ...current, effort }
    saveCandidates(next)
  }

  const removeCandidate = (index: number): void => {
    if (candidates.length === 1) return
    saveCandidates(candidates.filter((_, candidateIndex) => candidateIndex !== index))
  }

  const addCandidate = (key: string): void => {
    const option = selectableCatalog.find(
      (item) => modelTargetKey(item.provider, item.model) === key
    )
    if (!option) return
    saveCandidates([
      ...candidates,
      { provider: option.provider, model: option.model, effort: 'default' }
    ])
  }

  return (
    <section
      aria-labelledby={`model-policy-${taskClass}`}
      className="min-w-0 rounded-md border border-line bg-bg p-3"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 id={`model-policy-${taskClass}`} className="text-xs font-semibold text-ink">
            {label}
          </h4>
          <p className="text-xs text-ink-3">{description}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="xs"
          disabled={disabled}
          onClick={() => onSave({ taskClassRouting: { [taskClass]: null } })}
        >
          Reset to default
        </Button>
      </div>

      <div className="mt-3 flex flex-col gap-2">
        {candidates.map((candidate, index) => {
          const key = modelTargetKey(candidate.provider, candidate.model)
          const usedByOtherRows = new Set(
            candidates.flatMap((item, candidateIndex) =>
              candidateIndex === index ? [] : [modelTargetKey(item.provider, item.model)]
            )
          )
          const options = selectableCatalog.filter(
            (option) =>
              !usedByOtherRows.has(modelTargetKey(option.provider, option.model)) ||
              modelTargetKey(option.provider, option.model) === key
          )
          const optionGroups = providers.flatMap((provider) => {
            const providerOptions = options.filter((option) => option.provider === provider.name)
            return providerOptions.length === 0 ? [] : [{ provider, options: providerOptions }]
          })
          const candidateAllowed = isModelAllowed(policy, candidate.provider, candidate.model)
          const providerAvailable = providerByName.get(candidate.provider)?.available === true
          const candidateSelectable = candidateAllowed && providerAvailable
          const effortChoices = effortChoicesForModel(catalog, candidate.provider, candidate.model)
          const selectedEffort = effortChoices.some((option) => option.value === candidate.effort)
            ? candidate.effort
            : 'default'
          return (
            <div
              key={`${key}-${index}`}
              className="grid min-w-0 items-center gap-2 sm:grid-cols-[2rem_minmax(0,1fr)_minmax(8rem,10rem)_auto]"
            >
              <span className="text-center text-xs font-semibold text-ink-3" aria-hidden>
                {index + 1}.
              </span>
              <div className="min-w-0">
                <select
                  value={key}
                  onChange={(event) => selectModel(index, event.target.value)}
                  disabled={disabled || options.length === 0}
                  aria-label={`${label} candidate ${index + 1} model`}
                  className="h-9 min-w-0 w-full rounded-md border border-input bg-transparent px-3 text-sm text-ink outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50"
                >
                  {!candidateSelectable ? (
                    <option value={key}>
                      {modelLabel(policy, candidate.provider, candidate.model)} (
                      {providerByName.get(candidate.provider)?.label ?? candidate.provider}) —
                      skipped
                    </option>
                  ) : null}
                  {optionGroups.map(({ provider, options: providerOptions }) => (
                    <optgroup key={provider.name} label={provider.label}>
                      {providerOptions.map((option) => (
                        <option
                          key={modelTargetKey(option.provider, option.model)}
                          value={modelTargetKey(option.provider, option.model)}
                        >
                          {option.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {!candidateAllowed ? (
                  <span className="mt-1 block text-xs text-st-down">
                    Skipped — model is not allowed
                  </span>
                ) : !providerAvailable ? (
                  <span className="mt-1 block text-xs text-st-down">
                    Skipped — provider is unavailable
                  </span>
                ) : null}
              </div>
              <select
                value={selectedEffort}
                onChange={(event) => selectEffort(index, event.target.value as ModelEffort)}
                disabled={disabled}
                aria-label={`${label} candidate ${index + 1} effort`}
                className="h-9 min-w-0 w-full rounded-md border border-input bg-transparent px-3 text-sm text-ink outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50"
              >
                {effortChoices.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="flex shrink-0 justify-self-end gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Move candidate ${index + 1} up for ${label}`}
                  disabled={disabled || index === 0}
                  onClick={() => moveCandidate(index, -1)}
                >
                  <ArrowUp aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Move candidate ${index + 1} down for ${label}`}
                  disabled={disabled || index === candidates.length - 1}
                  onClick={() => moveCandidate(index, 1)}
                >
                  <ArrowDown aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Remove candidate ${index + 1} from ${label}`}
                  title={
                    candidates.length === 1
                      ? 'Each task class must keep at least one fallback candidate.'
                      : 'Remove fallback candidate'
                  }
                  disabled={disabled || candidates.length === 1}
                  onClick={() => removeCandidate(index)}
                >
                  <X aria-hidden />
                </Button>
              </div>
            </div>
          )
        })}
        <select
          value=""
          onChange={(event) => addCandidate(event.target.value)}
          disabled={
            disabled ||
            selectableCatalog.every((option) =>
              candidates.some(
                (candidate) =>
                  candidate.provider === option.provider && candidate.model === option.model
              )
            )
          }
          aria-label={`Add fallback for ${label}`}
          className="h-9 min-w-0 w-full rounded-md border border-dashed border-input bg-transparent px-3 text-sm text-ink outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50 sm:ml-10 sm:w-[calc(100%-2.5rem)]"
        >
          <option value="">Add fallback…</option>
          {providers.flatMap((provider) => {
            const options = selectableCatalog.filter(
              (option) =>
                option.provider === provider.name &&
                !candidates.some(
                  (candidate) =>
                    candidate.provider === option.provider && candidate.model === option.model
                )
            )
            return options.length === 0 ? (
              []
            ) : (
              <optgroup key={provider.name} label={provider.label}>
                {options.map((option) => (
                  <option
                    key={modelTargetKey(option.provider, option.model)}
                    value={modelTargetKey(option.provider, option.model)}
                  >
                    {option.label}
                  </option>
                ))}
              </optgroup>
            )
          })}
        </select>
      </div>

      <p className="mt-3 text-xs text-ink-3">
        Will use{' '}
        <span className="font-medium text-ink">
          {resolvedTarget
            ? `${modelLabel(policy, resolvedTarget.provider, resolvedTarget.model)} (${
                providers.find((provider) => provider.name === resolvedTarget.provider)?.label ??
                resolvedTarget.provider
              }) · ${
                config.resolvedEffort && config.resolvedEffort !== 'default'
                  ? effortLevelLabel(config.resolvedEffort)
                  : 'Provider default'
              }`
            : 'no configured model'}
        </span>
      </p>
    </section>
  )
}

/**
 * Org-wide task-class model routing. Visible only in debug mode. Provider and
 * model choices come entirely from the proxy policy; each class update is sent
 * independently.
 */
export function ModelSelectionCard() {
  const debugMode = useDebugMode()
  const {
    data: policy,
    isLoading,
    isError
  } = useGetModelPolicyQuery(undefined, {
    skip: !debugMode
  })
  const [updatePolicy, { isLoading: isSaving }] = useUpdateModelPolicyMutation()
  const [compactionDraft, setCompactionDraft] = useState('')
  const [compactionFractionDraft, setCompactionFractionDraft] = useState(
    String(defaultWindowSharePercent(FALLBACK_COMPACTION_DEFAULTS))
  )
  const [allowedModelDraft, setAllowedModelDraft] = useState<Set<string>>(new Set())
  const [deploymentDraft, setDeploymentDraft] = useState<Record<string, string>>({})
  const [saveError, setSaveError] = useState('')

  useEffect(() => {
    if (!policy) return
    setCompactionDraft(policy.auto_compaction_trigger_input_tokens?.toString() ?? '')
    setCompactionFractionDraft(
      fractionToPercentDraft(
        policy.auto_compaction_trigger_fraction ?? null,
        policy.compactionDefaults ?? FALLBACK_COMPACTION_DEFAULTS
      )
    )
    setAllowedModelDraft(
      new Set(policy.allowedModels.map((target) => modelTargetKey(target.provider, target.model)))
    )
    setDeploymentDraft(
      Object.fromEntries(
        policy.modelDeployments.map((item) => [
          modelTargetKey(item.provider, item.model),
          item.deployment
        ])
      )
    )
  }, [policy])

  if (!debugMode) return null

  if (isLoading) {
    return (
      <Card className="gap-3 py-4">
        <CardContent className="flex flex-col gap-1">
          <h3 className="text-sm font-semibold text-ink">Organization model defaults</h3>
          <p className="text-xs text-ink-3">Loading model policy…</p>
        </CardContent>
      </Card>
    )
  }

  if (isError || !policy) {
    return (
      <Card className="gap-3 py-4">
        <CardContent className="flex flex-col gap-1">
          <h3 className="text-sm font-semibold text-ink">Organization model defaults</h3>
          <p className="text-xs text-ink-3">
            Model policy is unavailable. Check the proxy connection and reload.
          </p>
        </CardContent>
      </Card>
    )
  }

  const providers = policy.providers

  const savePolicy = (payload: UpdateOrganizationModelPolicyPayload): void => {
    setSaveError('')
    void updatePolicy(payload)
      .unwrap()
      .catch(() => {
        setSaveError('Could not save model settings. Check your connection and try again.')
      })
  }

  const compactionBounds = policy.compactionDefaults ?? FALLBACK_COMPACTION_DEFAULTS
  const defaultSharePercent = defaultWindowSharePercent(compactionBounds)

  const saveTuning = (): void => {
    let trigger: number | null
    let fraction: number | null
    try {
      trigger = parseCompactionTrigger(compactionDraft, compactionBounds)
      fraction = parseCompactionFractionPercent(compactionFractionDraft, compactionBounds)
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Invalid auto-compaction threshold.')
      return
    }
    if (fraction === defaultSharePercent / 100 && policy.auto_compaction_trigger_fraction == null) {
      fraction = null
    }
    savePolicy({
      auto_compaction_trigger_input_tokens: trigger,
      auto_compaction_trigger_fraction: fraction
    })
  }

  const toggleAllowedModel = (provider: string, model: string, checked: boolean): void => {
    setSaveError('')
    setAllowedModelDraft((current) => {
      const next = new Set(current)
      const key = modelTargetKey(provider, model)
      if (checked) next.add(key)
      else next.delete(key)
      return next
    })
  }

  const saveAllowedModels = (): void => {
    const targets = new Map(
      [...policy.allowedModels, ...policy.catalog].map((target) => [
        modelTargetKey(target.provider, target.model),
        { provider: target.provider, model: target.model }
      ])
    )
    savePolicy({
      allowedModels: [...allowedModelDraft].flatMap((key) => {
        const target = targets.get(key)
        return target ? [target] : []
      })
    })
  }

  const saveDeployments = (): void => {
    savePolicy({
      modelDeployments: policy.catalog.flatMap((option) => {
        const deployment = (
          deploymentDraft[modelTargetKey(option.provider, option.model)] ?? ''
        ).trim()
        return deployment ? [{ provider: option.provider, model: option.model, deployment }] : []
      })
    })
  }

  const tuningDirty =
    compactionDraft.trim() !== (policy.auto_compaction_trigger_input_tokens?.toString() ?? '') ||
    compactionFractionDraft.trim() !==
      fractionToPercentDraft(policy.auto_compaction_trigger_fraction ?? null, compactionBounds)
  const persistedAllowedModelKeys = new Set(
    policy.allowedModels.map((target) => modelTargetKey(target.provider, target.model))
  )
  const allowedModelsDirty =
    allowedModelDraft.size !== persistedAllowedModelKeys.size ||
    [...allowedModelDraft].some((key) => !persistedAllowedModelKeys.has(key))
  const configuredAllowedModelCount = policy.catalog.filter((option) =>
    allowedModelDraft.has(modelTargetKey(option.provider, option.model))
  ).length
  const persistedDeployments = new Map(
    policy.modelDeployments.map((item) => [
      modelTargetKey(item.provider, item.model),
      item.deployment
    ])
  )
  const deploymentsDirty = policy.catalog.some((option) => {
    const key = modelTargetKey(option.provider, option.model)
    return (deploymentDraft[key] ?? '').trim() !== (persistedDeployments.get(key) ?? '')
  })

  return (
    <Card className="gap-3 py-4">
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <h3 className="text-sm font-semibold text-ink">Organization model defaults</h3>
          <p className="text-xs text-ink-3">
            Choose fallback routing, the models this organization may use, and optional host
            deployment names. Changes apply to everyone in your organization; individual chats can
            still pin a different allowed model.
          </p>
        </div>

        <Tabs defaultValue="fallback" className="gap-3">
          <TabsList variant="line" className="w-full">
            <TabsTrigger value="fallback">Fallback Settings</TabsTrigger>
            <TabsTrigger value="allowed">Allowed Models</TabsTrigger>
            <TabsTrigger value="deployments">Model Deployments</TabsTrigger>
          </TabsList>

          <TabsContent value="fallback" className="flex flex-col gap-3">
            <p className="text-xs text-ink-3">
              Requests start at #1 and try subsequent candidates in order when fallback is enabled
              and a provider is unavailable, rate limited, or returns an error. Disallowed models
              are skipped.
            </p>
            <div className="grid gap-3 lg:grid-cols-2">
              {POLICY_CLASSES.map((taskClass) => (
                <TaskClassRoutingBlock
                  key={taskClass.name}
                  taskClass={taskClass.name}
                  label={taskClass.label}
                  description={taskClass.description}
                  config={policy.taskClassRouting[taskClass.name]}
                  providers={providers}
                  catalog={policy.catalog}
                  policy={policy}
                  disabled={isSaving}
                  onSave={savePolicy}
                />
              ))}
            </div>

            <div className="flex flex-col gap-2 border-t border-line pt-3">
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-semibold text-ink">Organization model tuning</span>
                <span className="text-xs text-ink-3">
                  Thinking effort is set per candidate above. Auto-compaction stays
                  organization-wide. Later compaction can increase latency and cost.
                </span>
              </div>
              <label className="flex max-w-sm flex-col gap-1 text-xs font-medium text-ink">
                Auto-compaction window share (%)
                <Input
                  type="number"
                  min={Math.round(compactionBounds.minTriggerFraction * 100)}
                  max={Math.round(compactionBounds.maxTriggerFraction * 100)}
                  step={5}
                  value={compactionFractionDraft}
                  onChange={(event) => {
                    setSaveError('')
                    setCompactionFractionDraft(event.target.value)
                  }}
                  disabled={isSaving}
                  aria-label="Auto-compaction window share"
                  placeholder={`Product default (${defaultSharePercent}%)`}
                />
              </label>
              <label className="flex max-w-sm flex-col gap-1 text-xs font-medium text-ink">
                Auto-compaction threshold override (tokens)
                <Input
                  type="number"
                  min={compactionBounds.minTriggerInputTokens}
                  max={compactionBounds.maxTriggerInputTokens}
                  step={10_000}
                  value={compactionDraft}
                  onChange={(event) => {
                    setSaveError('')
                    setCompactionDraft(event.target.value)
                  }}
                  disabled={isSaving}
                  aria-label="Auto-compaction threshold"
                  placeholder="None"
                />
              </label>
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-ink-3">
                  The window share applies to every model relative to its own context window
                  (10–90%). An absolute threshold (50,000–800,000 tokens) overrides it. Both are
                  capped by each model&apos;s reply budget; leave both empty for the product default
                  ({defaultSharePercent}% of each model&apos;s window).
                </span>
                <Button
                  type="button"
                  size="xs"
                  onClick={saveTuning}
                  disabled={isSaving || !tuningDirty}
                >
                  Save tuning
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-line pt-3">
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-semibold text-ink">Provider fallback</span>
                <span className="text-xs text-ink-3">
                  If the first candidate cannot serve a request, try the next candidate in that task
                  class's order.
                </span>
              </div>
              <Switch
                checked={policy.fallback_enabled}
                onCheckedChange={(enabled) => savePolicy({ fallback_enabled: enabled })}
                disabled={isSaving}
                aria-label="Provider fallback"
              />
            </div>
          </TabsContent>

          <TabsContent value="allowed" className="flex flex-col gap-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <p className="text-xs text-ink-3">
                Only checked models can be used by task-class routing or selected in chat.
              </p>
              <Button
                type="button"
                size="xs"
                onClick={saveAllowedModels}
                disabled={isSaving || !allowedModelsDirty || configuredAllowedModelCount === 0}
              >
                Save allowed models
              </Button>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              {policy.providers.map((provider) => (
                <fieldset key={provider.name} className="flex min-w-0 flex-col gap-2">
                  <legend className="text-xs font-medium text-ink">{provider.label}</legend>
                  {policy.catalog
                    .filter((option) => option.provider === provider.name)
                    .map((option) => {
                      const key = modelTargetKey(option.provider, option.model)
                      const id = `allowed-model-${option.provider}-${option.model}`
                      return (
                        <label
                          key={key}
                          htmlFor={id}
                          className="flex min-w-0 items-center gap-2 text-xs text-ink-2"
                        >
                          <Checkbox
                            id={id}
                            checked={allowedModelDraft.has(key)}
                            onCheckedChange={(checked) =>
                              toggleAllowedModel(option.provider, option.model, checked === true)
                            }
                            disabled={isSaving}
                          />
                          <span className="truncate" title={option.label}>
                            {option.label}
                          </span>
                        </label>
                      )
                    })}
                </fieldset>
              ))}
            </div>
            {configuredAllowedModelCount === 0 ? (
              <p className="text-xs text-st-down">
                At least one model from a configured provider must remain allowed.
              </p>
            ) : null}
          </TabsContent>

          <TabsContent value="deployments" className="flex flex-col gap-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <p className="text-xs text-ink-3">
                Optional host-specific names for Azure AI Foundry and other OpenAI-compatible
                deployments. Leave a field blank to send the catalog id. Capability checks still use
                the catalog model.
              </p>
              <Button
                type="button"
                size="xs"
                onClick={saveDeployments}
                disabled={isSaving || !deploymentsDirty}
              >
                Save deployments
              </Button>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              {policy.providers.map((provider) => (
                <fieldset key={provider.name} className="flex min-w-0 flex-col gap-2">
                  <legend className="text-xs font-medium text-ink">{provider.label}</legend>
                  {policy.catalog
                    .filter((option) => option.provider === provider.name)
                    .map((option) => {
                      const key = modelTargetKey(option.provider, option.model)
                      return (
                        <label
                          key={key}
                          className="flex min-w-0 flex-col gap-1 text-xs font-medium text-ink"
                        >
                          <span className="truncate" title={option.label}>
                            {option.label}
                          </span>
                          <Input
                            value={deploymentDraft[key] ?? ''}
                            onChange={(event) => {
                              setSaveError('')
                              const nextValue = event.target.value
                              setDeploymentDraft((current) => ({
                                ...current,
                                [key]: nextValue
                              }))
                            }}
                            disabled={isSaving}
                            aria-label={`${option.label} deployment`}
                            placeholder={option.model}
                          />
                        </label>
                      )
                    })}
                </fieldset>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {saveError ? (
          <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
            {saveError}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
